'use client';

import React, { useState } from 'react';
import { Copy, Check } from 'lucide-react';
import { toast } from 'sonner';

interface CopyPromptButtonProps {
  prompt: string;
  variant?: 'default' | 'large' | 'small' | 'icon';
  className?: string;
}

export default function CopyPromptButton({
  prompt,
  variant = 'default',
  className = '',
}: CopyPromptButtonProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(prompt);
      setCopied(true);
      toast.success('Prompt copied to clipboard!', {
        description: 'Paste it into Midjourney, DALL·E, or Stable Diffusion.',
        duration: 3000,
      });
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error('Could not copy prompt. Please copy it manually.');
    }
  };

  if (variant === 'large') {
    return (
      <button
        onClick={handleCopy}
        className={`flex items-center justify-center gap-2.5 w-full py-3.5 px-6 rounded-xl font-semibold text-base transition-all duration-200 ${
          copied
            ? 'bg-green-500 text-white shadow-md'
            : 'bg-primary text-white hover:bg-primary-dark shadow-md hover:shadow-lg'
        } active:scale-95 ${className}`}
        aria-label="Copy prompt to clipboard"
      >
        {copied ? (
          <>
            <Check size={18} />
            Copied!
          </>
        ) : (
          <>
            <Copy size={18} />
            Copy Prompt
          </>
        )}
      </button>
    );
  }

  if (variant === 'small') {
    return (
      <button
        onClick={handleCopy}
        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all duration-200 ${
          copied
            ? 'bg-green-500 text-white' :'bg-primary text-white hover:bg-primary-dark'
        } active:scale-95 ${className}`}
        aria-label="Copy prompt"
      >
        {copied ? <Check size={12} /> : <Copy size={12} />}
        {copied ? 'Copied!' : 'Copy'}
      </button>
    );
  }

  if (variant === 'icon') {
    return (
      <button
        onClick={handleCopy}
        className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-200 ${
          copied
            ? 'bg-green-500 text-white' :'bg-muted text-muted-foreground hover:bg-primary hover:text-white'
        } active:scale-95 ${className}`}
        aria-label="Copy prompt"
        title="Copy prompt"
      >
        {copied ? <Check size={14} /> : <Copy size={14} />}
      </button>
    );
  }

  return (
    <button
      onClick={handleCopy}
      className={`flex items-center gap-2 px-4 py-2 rounded-full font-semibold text-sm transition-all duration-200 ${
        copied
          ? 'bg-green-500 text-white' :'bg-primary text-white hover:bg-primary-dark'
      } active:scale-95 shadow-sm hover:shadow-md ${className}`}
      aria-label="Copy prompt to clipboard"
    >
      {copied ? <Check size={14} /> : <Copy size={14} />}
      {copied ? 'Copied!' : 'Copy Prompt'}
    </button>
  );
}